import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import {
  updateCartItems,
  selectAllCartItems,
  selectCartItemsByCurrencyAndMerchant,
  selectCartCurrencies,
  selectCartLoading,
  selectCartError,
  selectCartUpdating,
  setCartError
} from '../../redux/slices/cartSlice';
import { resetCheckout } from '../../redux/slices/checkoutSlice';
import { useGetCartQuery } from '../../redux/apis';

// Import components
import CartEmptyState from './components/CartEmptyState';
import CurrencyCheckoutSection from './CurrencyCheckoutSection';
import { DeleteConfirmationModal } from './components/CartModals';

// Define colors based on brand palette
const errorBgColor = 'bg-red-100'; // Keep standard red for errors? Or maybe a lighter orange/beige? Let's stick to red for now.
const errorTextColor = 'text-red-700';
const errorBorderColor = 'border-red-400';
const primaryButtonBg = 'bg-brand-teal';
const primaryButtonHoverBg = 'hover:bg-brand-teal-dark';
const primaryButtonText = 'text-white';

const CartPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  // Local state for modals
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [targetItem, setTargetItem] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Local state for checkout process (still relevant per currency)
  const [isCheckingOut, setIsCheckingOut] = useState(false); // This might be per currency if UI supports it
  const [checkoutError, setCheckoutError] = useState(null);

  // Trigger cart refetch on mount and when addCartSuccess changes (e.g., after adding an item)
  // The useGetCartQuery hook itself handles caching and refetching based on tag invalidation.
  const { isLoading: queryIsLoading, error: queryError, refetch: refetchCart } = useGetCartQuery();

  // Select cart data from Redux store using new selectors
  const allCartItems = useSelector(selectAllCartItems);
  const groupedCartItems = useSelector(selectCartItemsByCurrencyAndMerchant);
  const currencies = useSelector(selectCartCurrencies);
  const isLoading = useSelector(selectCartLoading) || queryIsLoading;
  const cartError = useSelector(selectCartError) || queryError?.data?.message;
  const updating = useSelector(selectCartUpdating);

  // Memoize hasMultipleCurrencies
  const hasMultipleCurrencies = useMemo(() => currencies.length > 1, [currencies]);

  // Handle delete button click
  const handleDelete = (product) => {
    setTargetItem(product); 
    setDeleteModalOpen(true); 
  };

  // Handle confirm delete action from modal
  const handleConfirmDelete = async () => {
    if (!targetItem) return;

    // targetItem should have cartItemId (the component ID)
    // and cartItemIndex (original index from backend cartItems array)
    if (!targetItem.cartItemId) {
      dispatch(setCartError('Failed to delete item. Item component ID is missing.'));
      setDeleteModalOpen(false);
      setTargetItem(null);
      return;
    }
    
    try {
      setDeleteLoading(true);
      // The thunk now primarily uses cartItemId for deletion
      await dispatch(updateCartItems({ 
        cartItemId: targetItem.cartItemId, 
        action: 'delete', 
        // cartItemIndex might still be useful if thunk has fallback or for other actions
        cartItemIndex: targetItem.cartItemIndex 
      }));
      // Cart will refetch due to tag invalidation by removeCartItem endpoint
    } catch (error) {
      dispatch(setCartError(error.message || 'Failed to delete item.'));
    } finally {
      setDeleteLoading(false);
    setDeleteModalOpen(false);
    setTargetItem(null);
    }
  };

  // Handle quantity change
  const handleQuantityChange = (product, action) => {
    if (action === 'decrease' && product.quantity <= 1) {
      setTargetItem(product);
      setDeleteModalOpen(true);
      return;
    }

    const stock = parseInt(product.stock, 10); // product.stock should be directly available
    if (action === 'increase' && product.quantity >= stock && !isNaN(stock)) {
      // We'll show a popup tooltip instead of modal
      // The StockLimitModal is no longer used
      return;
    }
    
    // The thunk needs cartItemId or cartItemIndex to identify the item
    dispatch(updateCartItems({ 
      cartItemId: product.cartItemId, 
      cartItemIndex: product.cartItemIndex, // original index from backend for API
      action 
    }));
    // Cart will refetch due to tag invalidation by updateCartItem endpoint
  };

  useEffect(() => {
    return () => {
      dispatch(resetCheckout());
    };
  }, [dispatch]);

  const handleCheckout = (currency) => {
    setIsCheckingOut(true); // This could be specific to the currency section later
    setCheckoutError(null);
    try {
      localStorage.setItem('checkoutCurrency', currency);
      history.push('/checkout');
    } catch (error) {
      setCheckoutError(error.message || 'Error during checkout preparation.');
      setIsCheckingOut(false);
    }
  };

  if (isLoading && allCartItems.length === 0) { // Show loader if loading and no items yet
    return (
      <div className="min-h-screen flex justify-center items-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!isLoading && allCartItems.length === 0 && !cartError) {
    return <CartEmptyState />; // Removed activeTab and setActiveTab props
  }

  return (
    <div className="bg-gray-50 min-h-screen py-6 sm:py-8 lg:py-12">
      <div className="max-w-screen-lg mx-auto px-4 md:px-8">
        {/* Simplified Header or Title */}
        <div className="mb-6 text-center"><h1 className="text-3xl font-bold text-gray-800">Your Cart</h1></div>
        
        {cartError && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{typeof cartError === 'string' ? cartError : JSON.stringify(cartError)}</p>
              </div>
            </div>
          </div>
        )}

        {hasMultipleCurrencies && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  Your cart contains items in different currencies. You'll need to check out each currency group separately.
                </p>
              </div>
            </div>
        </div>
      )}

        {/* Iterate by currency */}
        {currencies.map((currency, index) => {
          const merchantGroupsForCurrency = groupedCartItems[currency] || {};
          const currencyItems = Object.values(merchantGroupsForCurrency).flatMap(group => group.products);

          if (currencyItems.length === 0) return null;
          
          return (
            <CurrencyCheckoutSection
              key={currency}
              currency={currency}
              // group prop might be deprecated or changed if merchantGroupsForCurrency is directly used by CurrencyCheckoutSection
              // For now, passing items for this currency, CurrencyCheckoutSection might internally use merchantGroupsForCurrency
              itemsForCurrency={currencyItems} 
              merchantGroups={merchantGroupsForCurrency} // Pass the merchant groups for this currency
              // activeTab prop removed
                  handleDelete={handleDelete}
                  handleQuantityChange={handleQuantityChange}
              isCheckingOut={isCheckingOut} // This might need to become currency-specific if UI allows parallel checkouts
                updating={updating}
              checkoutError={checkoutError} // Also potentially currency-specific
              handleCheckout={handleCheckout} // handleCheckout already takes currency
              isFirstSection={index === 0}
              hasMultipleCurrencies={hasMultipleCurrencies}
              />
          );
        })}
      </div>

      <DeleteConfirmationModal 
        isOpen={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setTargetItem(null); }}
        onConfirm={handleConfirmDelete}
        isLoading={deleteLoading}
      />
    </div>
  );
};

export default CartPage; 